import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import {
  ArrowRight, FileText, Brain, Target, TrendingUp,
  Upload, ClipboardList, Fingerprint, Rocket, CheckCircle
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function HowItWorksPage() {
  const navigate = useNavigate();

  const steps = [
    {
      step: '01', icon: Upload, color: '#3B82F6',
      title: 'Upload Your Resume',
      desc: 'Start by uploading your resume. Kairera parses your experience, scores it against ATS standards, and builds the foundation of your career profile.',
      details: ['Instant ATS compatibility score', 'Automatic skill extraction', 'Experience timeline mapping', 'Identifies strengths and gaps'],
      image: '/mockups/resume-mock.jpg',
    },
    {
      step: '02', icon: ClipboardList, color: '#0D9488',
      title: 'Take Career Assessments',
      desc: 'Answer targeted discovery questions and complete structured assessment batteries. These aren\'t personality quizzes. They\'re career-science instruments that map your decision patterns.',
      details: ['16-question discovery phase', '7 assessment batteries', 'Multiple input formats', 'Progress saved automatically'],
      image: '/mockups/assessment-mock.jpg',
    },
    {
      step: '03', icon: Fingerprint, color: '#805AD5',
      title: 'Unlock Your Career Persona',
      desc: 'Your responses are analyzed across 12 career dimensions. You receive a unique Career Persona, one of 8 archetypes that maps your decision-making DNA.',
      details: ['12-dimension grading', '8 career archetypes', 'Strength/blind spot mapping', 'Evolving confidence scores'],
      image: '/mockups/dashboard-mock.jpg',
    },
    {
      step: '04', icon: Rocket, color: '#F5841F',
      title: 'Get Your Career Strategy',
      desc: 'Your expert panel generates personalized reports, a grade report card, and actionable next steps. Use career tools to simulate decisions and negotiate with confidence.',
      details: ['Personalized career reports', 'Expert panel recommendations', 'Decision calculator', 'Salary negotiation simulator'],
      image: '/mockups/dashboard-mock.jpg',
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-3" style={{ color: '#0D9488' }}>
              How It Works
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-5" style={{ color: '#0D1E3A' }}>
              From resume to<br /><span style={{ color: '#0D9488' }}>career strategy</span>
            </h1>
            <p className="text-base lg:text-lg max-w-2xl mx-auto" style={{ color: '#64748b' }}>
              Four structured steps that transform your career data into actionable intelligence.
              No fluff, no generic advice. Just strategy built from your unique profile.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-6">
          {steps.map((step, i) => (
            <motion.div key={step.step} {...fadeUp} transition={{ delay: 0.1 }}
              className={`flex flex-col ${i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center py-16 ${i < steps.length - 1 ? 'border-b' : ''}`}
              style={{ borderColor: '#f1f5f9' }}>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${step.color}10` }}>
                    <step.icon className="w-5 h-5" style={{ color: step.color }} />
                  </div>
                  <span className="text-[10px] font-bold tracking-[0.25em]" style={{ color: '#94a3b8' }}>
                    STEP {step.step}
                  </span>
                </div>
                <h3 className="text-2xl font-extrabold mb-3" style={{ color: '#0D1E3A' }}>{step.title}</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: '#64748b' }}>{step.desc}</p>
                <ul className="space-y-2.5">
                  {step.details.map(d => (
                    <li key={d} className="flex items-center gap-2.5">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: step.color }} />
                      <span className="text-sm" style={{ color: '#475569' }}>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex-1 w-full">
                <div className="rounded-xl overflow-hidden shadow-lg" style={{ border: '1px solid #e2e8f0' }}>
                  <img src={step.image} alt={step.title} className="w-full h-auto" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-5" style={{ color: '#0D1E3A' }}>
              Ready to start?
            </h2>
            <p className="text-base mb-8" style={{ color: '#64748b' }}>
              Create your free account and take the first step toward career clarity.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/register')}
                className="h-13 px-8 text-base font-bold rounded-xl"
                style={{ background: '#0D9488', color: '#fff', height: '52px' }}>
                Start Free <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => navigate('/features')}
                className="h-13 px-8 text-base font-medium rounded-xl"
                style={{ height: '52px', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                Explore Features
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
